import React, { FormEvent, useEffect, useState, useContext } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shield, Check, XCircle, Mail, ArrowRight, Lock, CheckCircle2 } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';
import { ApiApi } from 'api-client';
import { getApiConfiguration } from '../../api/utils';
import { Button } from '../../components/ui/button';

const VerifyEmail: React.FC = () => {
    const { setUserDetails } = useContext(AuthContext);
    const [searchParams] = useSearchParams();
    const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
    const [fieldErrors, setFieldErrors] = useState<{[key: string]: string[]}>({});
    const [message, setMessage] = useState('');

    const verifyEmail = async (key: string) => {
        try {
            const client = new ApiApi(getApiConfiguration());
            await client.apiAuthVerifyEmailCreate({
                verifyEmail: { key }
            });

            setStatus('success');
            setMessage('Your email has been verified successfully!');
        } catch (error: any) {
            const errorData = await error.response?.json();
            setStatus('error');
            setMessage(errorData?.detail || 'Failed to verify email. Please try again or request a new verification link.');
        }
    };

    useEffect(() => {
        const key = searchParams.get('key');
        
        if (!key) {
            setStatus('error');
            setMessage('Verification key is missing');
            return;
        }

        verifyEmail(key);
    }, [searchParams]);

    return (
        <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
                    {/* Header */}
                    <div className="flex items-center justify-center mb-8">
                        <Shield className="h-10 w-10 text-blue-500" />
                        <h1 className="text-2xl font-bold text-white ml-3">Flawtrack</h1>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h2 className="text-xl font-semibold text-white">Email Verification</h2>
                        <p className="text-sm text-gray-400 mt-1">Verifying your email address</p>
                    </div>

                    {/* Status Content */}
                    <div className="text-center">
                        {status === 'verifying' && (
                            <div className="flex flex-col items-center justify-center space-y-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                <span className="text-gray-300">Verifying your email...</span>
                            </div>
                        )}



                        {status === 'success' && (
                            <div className="space-y-6 text-center">
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <CheckCircle2 className="w-16 h-16 text-green-500" />
                                    <h3 className="text-xl font-semibold text-white">Email Verified Successfully!</h3>
                                    <div className="p-4 bg-green-500/10 text-green-500 rounded-lg border border-green-500/20 w-full">
                                        <p className="font-medium">{message}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <Link 
                                        to="/login?trial=true"
                                        className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        Continue to Login
                                    </Link>
                                    <p className="text-sm text-gray-400">You can now log in to your account</p>
                                </div>
                            </div>
                        )}

                        {status === 'error' && (
                            <div className="space-y-6 text-center">
                                <div className="flex flex-col items-center justify-center space-y-4">
                                    <XCircle className="w-16 h-16 text-red-500" />
                                    <h3 className="text-xl font-semibold text-white">Verification Failed</h3>
                                    <div className="p-4 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20 w-full">
                                        <p className="font-medium">{message}</p>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <Link 
                                        to="/resend-verification"
                                        className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                    >
                                        <Mail className="w-4 h-4 mr-2" />
                                        Resend Verification Email
                                    </Link>
                                    <Link 
                                        to="/login"
                                        className="inline-flex items-center justify-center w-full px-4 py-2 bg-transparent border border-gray-800 text-white rounded-lg hover:bg-gray-800/50 transition-colors"
                                    >
                                        <ArrowRight className="w-4 h-4 mr-2" />
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VerifyEmail; 